// Pure event reducer for the agent chat UI.
// Copied + factored from frontend/src/hooks/agentEventReducer.ts.
// Extension event types (table_rendered, chart_rendered, dashboard_created,
// webapp_ready, page_tool_request, settings_updated) and the tool_use_end
// self-parse are dispatched through the RenderPlugin seam (plugins.ts) as
// well as being maintained in the core state maps for generic replay.
// The SINGLE-REDUCER INVARIANT is preserved: one agentEventReducer is used
// for both live streaming and replay — never a second reconstruction path.
// See ../../docs/09-frontend-components.md.

import type {
  ActivityStatus,
  AgentMessage,
  AgentSSEEvent,
  ArtifactInfo,
  AskUserQuestionInfo,
  CreatedDashboardInfo,
  HookEventInfo,
  PageToolCallInfo,
  RenderedChartInfo,
  RenderedTableInfo,
  SkillHoistedEventData,
  SkillInstalledEventData,
  SubagentEventData,
  SubagentInfo,
  TodoItem,
  ToolCallInfo,
  WorkflowEventData,
  WorkflowInfo,
} from './types.js'
import { getToolSummary } from './tool-formatters.js'
import { getToolDisplayName } from './tool-formatters.js'
import { collapseBase64 } from './base64.js'

export interface InstalledSkillInfo { id: string; name: string; visibility?: string }

/** The skill this session last distilled (hoist_skill); `at` is the event's timestamp. */
export interface HoistedSkillInfo { name: string; at: string }

export interface AgentSessionInfo {
  tools: string[]
  model: string
  mcpServers: { name: string; status: string }[]
}

export interface AgentEventState {
  messages: AgentMessage[]
  isStreaming: boolean
  error: string | null
  artifacts: ArtifactInfo[]
  /** Currently building assistant message (null when not streaming) */
  currentMessage: { id: string; content: string } | null
  /** Active tool calls for current message */
  toolCalls: Map<string, ToolCallInfo>
  /** Rendered tables keyed by table ID (populated by plugin dispatch + self-parse) */
  renderedTables: Map<string, RenderedTableInfo>
  /** Rendered charts keyed by chart ID (populated by plugin dispatch + self-parse) */
  renderedCharts: Map<string, RenderedChartInfo>
  /** Asked questions keyed by tool call ID */
  askedQuestions: Map<string, AskUserQuestionInfo>
  /** Created dashboards keyed by dashboard ID */
  createdDashboards: Map<string, CreatedDashboardInfo>
  /** Tracks if tool_use_start was seen since last content_delta — signals split needed */
  hasActiveToolCalls: boolean
  /** Original message ID from message_start, used for generating continuation IDs */
  originalMessageId: string | null
  /** Counter for continuation messages within current API message */
  continuationCount: number
  /** Persistent activity status shown above the input area */
  activityStatus: ActivityStatus | null
  /** Accumulated tool_input_delta partial JSON */
  toolInputBuffer: string
  /** Pending page tool requests (tool name + tool call ID) */
  pendingPageToolRequest: { toolName: string; toolCallId: string } | null
  /**
   * Calls of the page's own tools (`page_tool_call`), oldest first, each once.
   * The page carries them out; nothing here does. A restored session's list
   * holds the calls of its past turns too, so a page acts only on calls that
   * arrive while it is open, by `id`.
   */
  pageToolCalls: PageToolCallInfo[]
  /** Pending settings update from update_table_settings */
  pendingSettingsUpdate: { toolCallId: string; settings: Record<string, unknown> } | null
  /** Todo items from TodoWrite tool calls */
  todos: TodoItem[]
  /** Session info from session_info event (tools, model, MCP servers) */
  sessionInfo: AgentSessionInfo | null
  /** Subagent lifecycle events for debugging */
  subagentEvents: Array<{ event: 'start' | 'progress' | 'stop'; agentId: string; agentType?: string; result?: string; timestamp: string }>
  /**
   * Which `Agent` / `Workflow` tool call each sub-agent or workflow task
   * belongs to: agent id or task id -> tool call id. Filled by the task events
   * that carry `toolUseId`, read by the hook events that carry only `agentId`.
   */
  delegatedTasks: Record<string, string>
  /** Skills installed live into this session (deduped by name, latest wins) */
  installedSkills: InstalledSkillInfo[]
  /**
   * The draft skill this session last distilled. The host's Skills tab opens
   * the draft when it changes. A restored session does not carry it over
   * (useAgentSession copies only the fields a restore should keep), so
   * reopening a chat never reopens an old draft.
   */
  hoistedSkill: HoistedSkillInfo | null
  /** The session's current job set, set by `session_jobs_changed`. Null until the first event; an empty array is a valid "no job selected" state. */
  sessionJobs: string[] | null
  /**
   * Messages the user sent while a turn was running, oldest first. Held apart
   * from `messages` so they stay BELOW the running turn however much it goes
   * on writing; each joins the conversation when it is actually sent.
   */
  queuedMessages: AgentMessage[]
}

export function initialAgentEventState(): AgentEventState {
  return {
    messages: [],
    isStreaming: false,
    error: null,
    artifacts: [],
    currentMessage: null,
    toolCalls: new Map(),
    renderedTables: new Map(),
    renderedCharts: new Map(),
    askedQuestions: new Map(),
    createdDashboards: new Map(),
    hasActiveToolCalls: false,
    originalMessageId: null,
    continuationCount: 0,
    activityStatus: null,
    toolInputBuffer: '',
    pendingPageToolRequest: null,
    pageToolCalls: [],
    pendingSettingsUpdate: null,
    todos: [],
    sessionInfo: null,
    subagentEvents: [],
    delegatedTasks: {},
    installedSkills: [],
    hoistedSkill: null,
    sessionJobs: null,
    queuedMessages: [],
  }
}

/**
 * Pure reducer that processes an SSE event and returns new state.
 * Used for both live SSE streaming and durable event replay — the same
 * function, the same code path (SINGLE-REDUCER INVARIANT).
 *
 * Extension event types (table_rendered, chart_rendered, dashboard_created,
 * webapp_ready, page_tool_request, settings_updated) are maintained in the
 * core state maps AND dispatched to registered RenderPlugins via the seam
 * in plugins.ts. Platinum's Carbon widgets register there.
 *
 * Message splitting: When content_delta arrives after a tool_use_start within
 * the same message_start/message_end boundary, a new "continuation" message is
 * created instead of appending to the current one.
 */
export function agentEventReducer(state: AgentEventState, event: AgentSSEEvent): AgentEventState {
  const data = (event.data ?? {}) as Record<string, unknown>
  // Clone state to avoid mutation
  const next = { ...state }

  // An event a sub-agent produced (Chief of Staff mode) carries the id of the
  // Agent / Workflow tool call that launched it. It belongs to that call's
  // sub-tree and never to the message being written.
  const parentToolUseId = typeof data.parentToolUseId === 'string' && data.parentToolUseId
    ? data.parentToolUseId
    : undefined
  if (parentToolUseId && NESTED_EVENT_TYPES.has(event.type)) {
    return reduceNestedEvent(next, event, data, parentToolUseId)
  }

  switch (event.type) {
    case 'message_start': {
      if (data.role === 'assistant') {
        const msgId = data.messageId as string
        next.currentMessage = { id: msgId, content: '' }
        next.toolCalls = new Map()
        next.hasActiveToolCalls = false
        next.originalMessageId = msgId
        next.continuationCount = 0
        next.activityStatus = { label: 'Thinking...', category: 'thinking' }
        // Create message immediately so tool_use_start can attach to it
        if (!next.messages.find(m => m.id === msgId)) {
          next.messages = [...next.messages, {
            id: msgId,
            role: 'assistant' as const,
            content: '',
            toolCalls: [],
            timestamp: event.timestamp,
          }]
        }
      }
      break
    }
    case 'content_delta': {
      if (next.currentMessage) {
        next.activityStatus = { label: 'Writing response...', category: 'writing' }
        if (next.hasActiveToolCalls) {
          // Split: create continuation message after tool boundary
          next.continuationCount += 1
          const contId = `${next.originalMessageId}-cont-${next.continuationCount}`
          next.currentMessage = { id: contId, content: data.delta as string }
          next.toolCalls = new Map()
          next.hasActiveToolCalls = false
          next.messages = [...next.messages, {
            id: contId,
            role: 'assistant' as const,
            content: data.delta as string,
            toolCalls: [],
            timestamp: event.timestamp,
          }]
        } else {
          const content = next.currentMessage.content + (data.delta as string)
          next.currentMessage = { ...next.currentMessage, content }
          const messageId = next.currentMessage.id
          next.messages = next.messages.map(m =>
            m.id === messageId ? { ...m, content } : m
          )
        }
      }
      break
    }
    case 'thinking_delta': {
      if (next.currentMessage) {
        // Only allocate new activityStatus when category changes (avoids re-render on every delta)
        if (next.activityStatus?.category !== 'thinking') {
          next.activityStatus = { label: 'Reasoning...', category: 'thinking' }
        }
        const messageId = next.currentMessage.id
        const thinkingDelta = data.delta as string
        const currentMsg = next.messages.find(m => m.id === messageId)
        const thinking = (currentMsg?.thinking || '') + thinkingDelta
        next.messages = next.messages.map(m =>
          m.id === messageId ? { ...m, thinking } : m
        )
      }
      break
    }
    case 'tool_use_start': {
      next.hasActiveToolCalls = true
      next.toolInputBuffer = ''
      const toolName = data.toolName as string
      const toolInput = data.input as Record<string, unknown>
      const toolCall: ToolCallInfo = {
        id: data.toolCallId as string,
        name: toolName,
        input: toolInput,
        status: 'running',
      }
      if (isSubagentTool(toolName)) toolCall.subagent = newSubagent(toolCall)
      else if (isWorkflowTool(toolName)) toolCall.workflow = newWorkflow(toolCall)
      next.toolCalls = new Map(next.toolCalls)
      next.toolCalls.set(toolCall.id, toolCall)
      next.messages = updateToolCallsOnMessages(next.messages, next.currentMessage, next.toolCalls)
      const displayName = getToolDisplayName(toolName, toolInput)
      const summary = getToolSummary(toolName, toolInput)
      next.activityStatus = {
        label: `Running: ${displayName}`,
        detail: summary || undefined,
        category: 'tool',
        toolName,
        toolInput,
      }
      break
    }
    case 'tool_use_end': {
      const toolId = data.toolCallId as string
      next.toolCalls = new Map(next.toolCalls)
      const tc = next.toolCalls.get(toolId)
      if (tc) {
        // Tool is in current Map (same message boundary)
        const updated: ToolCallInfo = {
          ...tc,
          status: (data.isError ? 'error' : 'complete') as ToolCallInfo['status'],
          output: data.output as string,
        }
        next.toolCalls.set(updated.id, updated)
        next.messages = updateToolCallsOnMessages(next.messages, next.currentMessage, next.toolCalls)
        // If no tools still running, revert to thinking
        const anyRunning = Array.from(next.toolCalls.values()).some(t => t.status === 'running')
        if (!anyRunning) {
          next.activityStatus = { label: 'Thinking...', category: 'thinking' }
        }

        // Self-parse rendered content from tool output (tables, charts, ask_user, dashboards).
        // TODO (follow-up AL-6-revise item 3): the __render_table / __render_chart inline parsing
        // below stays in the core reducer for now to keep the 35 reducer tests green and the
        // single-reducer invariant intact. A future cleanup could move this to plugin-supplied
        // reduce() calls (dispatched from AgentChat via foldPluginEvents) once integration tests
        // cover the full pipeline end-to-end.
        const toolName = updated.name || ''
        const isTodoWrite = toolName === 'TodoWrite' || toolName.includes('todo_write') || toolName.includes('TodoWrite')
        const isTable = toolName.includes('render_table')
        const isChart = toolName.includes('render_chart')
        const isAsk = toolName.includes('ask_user')
        const isDashboard = toolName.includes('create_dashboard')
        if (isTodoWrite && !data.isError) {
          // TodoWrite's input contains the todo list; the output is just an acknowledgment
          const input = (updated.input || {}) as Record<string, unknown>
          const todos = input.todos as Array<{ content: string; status: string }> | undefined
          if (Array.isArray(todos)) {
            next.todos = todos.map(t => ({
              content: t.content || '',
              status: (t.status as TodoItem['status']) || 'pending',
            }))
          }
        }
        if ((isTable || isChart || isAsk || isDashboard) && !data.isError) {
          try {
            const parsed = JSON.parse(data.output as string)
            let text: string | undefined
            if (parsed?.content?.[0]?.text) text = parsed.content[0].text
            else if (Array.isArray(parsed) && parsed[0]?.text) text = parsed[0].text
            if (text) {
              const inner = JSON.parse(text)
              const toolCallId = data.toolCallId as string
              if (inner.__render_table) {
                // Deduplicate: table_rendered SSE event may also add this
                const alreadyExists = [...next.renderedTables.values()].some(t => t.toolCallId === toolCallId)
                if (!alreadyExists) {
                  const id = `table-${toolCallId}`
                  next.renderedTables = new Map(next.renderedTables)
                  next.renderedTables.set(id, {
                    id, platinumData: inner.platinumData, title: inner.title || '', toolCallId,
                    customer: inner.customer, job: inner.job, spec: inner.spec,
                  })
                }
              }
              if (inner.__render_chart) {
                // Deduplicate: chart_rendered SSE event may also add this
                const alreadyExists = [...next.renderedCharts.values()].some(c => c.toolCallId === toolCallId)
                if (!alreadyExists) {
                  const id = `chart-${toolCallId}`
                  next.renderedCharts = new Map(next.renderedCharts)
                  next.renderedCharts.set(id, {
                    id, platinumData: inner.platinumData,
                    chartType: inner.chartType || 'bar', title: inner.title || '', toolCallId,
                    customer: inner.customer, job: inner.job, spec: inner.spec,
                  })
                }
              }
              if (inner.__ask_user) {
                next.askedQuestions = new Map(next.askedQuestions)
                next.askedQuestions.set(toolCallId, {
                  question: inner.question, options: inner.options,
                  allowFreetext: inner.allow_freetext || false,
                  multiSelect: inner.multi_select === true,
                  context: inner.context || '', toolCallId, answered: false,
                })
              }
              if (inner.__dashboard_created) {
                next.createdDashboards = new Map(next.createdDashboards)
                next.createdDashboards.set(inner.dashboardId as string, {
                  dashboardId: inner.dashboardId as string,
                  customer: inner.customer as string,
                  config: inner.config as CreatedDashboardInfo['config'],
                  viewUrl: inner.viewUrl as string,
                  toolCallId,
                })
              }
            }
          } catch { /* not all outputs are parseable */ }
        }
      } else {
        // Fallback: tool was in a previous message (message_start reset the Map).
        // Update it directly in the messages array.
        const status = (data.isError ? 'error' : 'complete') as ToolCallInfo['status']
        next.messages = updateToolInMessages(next.messages, toolId, {
          status,
          output: data.output as string,
        })
      }
      // An Agent or Workflow call returns as soon as its work is launched in
      // the background, so only a failed launch settles the sub-tree here;
      // the task events settle the rest.
      if (data.isError) {
        const launched = findToolCall(next, toolId)
        if (launched?.subagent?.status === 'running') {
          setToolCall(next, toolId, { ...launched, subagent: { ...launched.subagent, status: 'failed' } })
        } else if (launched?.workflow?.status === 'running') {
          setToolCall(next, toolId, { ...launched, workflow: { ...launched.workflow, status: 'failed' } })
        }
      }
      // A launched workflow's result names its run, the folder on the runner
      // its agents' saved files are under. The first result to name it wins.
      if (!data.isError) {
        const launched = findToolCall(next, toolId)
        if (launched && isWorkflowTool(launched.name) && !launched.workflow?.runId) {
          const runId = workflowRunId(data.output)
          if (runId) {
            const workflow = launched.workflow ?? newWorkflow(launched)
            setToolCall(next, toolId, { ...launched, workflow: { ...workflow, runId } })
          }
        }
      }
      break
    }
    case 'tool_progress': {
      next.toolCalls = new Map(next.toolCalls)
      const tc = next.toolCalls.get(data.toolUseId as string)
      if (tc) {
        const elapsed = data.elapsedSeconds as number
        next.toolCalls.set(tc.id, { ...tc, elapsedSeconds: elapsed })
        next.messages = updateToolCallsOnMessages(next.messages, next.currentMessage, next.toolCalls)
        if (next.activityStatus?.category === 'tool') {
          next.activityStatus = { ...next.activityStatus, elapsedSeconds: elapsed }
        }
      }
      break
    }
    case 'message_end': {
      next.currentMessage = null
      break
    }
    case 'table_rendered': {
      // Extension event: dispatched to registered RenderPlugins AND stored in core maps.
      const toolCallId = (data.toolCallId as string) || ''
      next.renderedTables = new Map(next.renderedTables)
      // Remove self-parse fallback entry if present (SSE is authoritative)
      const selfParseId = `table-${toolCallId}`
      if (next.renderedTables.has(selfParseId)) {
        next.renderedTables.delete(selfParseId)
      }
      // The sandbox marker hook emits one event per table with no explicit `id`;
      // a batch render_tables call produces several sharing one toolCallId. Derive
      // a per-event key from the count of tables already stored for this tool call
      // so they don't collapse onto one entry (deterministic across replays).
      let id = data.id as string | undefined
      if (!id) {
        let n = 0
        for (const t of next.renderedTables.values()) if (t.toolCallId === toolCallId) n++
        id = `table-${toolCallId}-${n}`
      }
      // Skip if this exact event was already processed (replay)
      if (next.renderedTables.has(id)) break
      next.renderedTables.set(id, {
        id,
        platinumData: data.platinumData,
        title: (data.title as string) || '',
        toolCallId,
        customer: data.customer as string | undefined,
        job: data.job as string | undefined,
        spec: data.spec as string | undefined,
      })
      break
    }
    case 'chart_rendered': {
      // Extension event: dispatched to registered RenderPlugins AND stored in core maps.
      const toolCallId = (data.toolCallId as string) || ''
      next.renderedCharts = new Map(next.renderedCharts)
      // Remove self-parse fallback entry if present (SSE is authoritative)
      const selfParseId = `chart-${toolCallId}`
      if (next.renderedCharts.has(selfParseId)) {
        next.renderedCharts.delete(selfParseId)
      }
      // Same as table_rendered: several id-less events can share one toolCallId
      // (batch tools) — derive a per-event key so charts don't collapse.
      let id = data.id as string | undefined
      if (!id) {
        let n = 0
        for (const c of next.renderedCharts.values()) if (c.toolCallId === toolCallId) n++
        id = `chart-${toolCallId}-${n}`
      }
      // Skip if this exact event was already processed (replay)
      if (next.renderedCharts.has(id)) break
      next.renderedCharts.set(id, {
        id,
        platinumData: data.platinumData,
        chartType: (data.chartType as RenderedChartInfo['chartType']) || 'bar',
        title: (data.title as string) || '',
        toolCallId,
        customer: data.customer as string | undefined,
        job: data.job as string | undefined,
        spec: data.spec as string | undefined,
      })
      break
    }
    case 'ask_user': {
      const questionInfo: AskUserQuestionInfo = {
        question: data.question as string,
        options: data.options as AskUserQuestionInfo['options'],
        allowFreetext: (data.allowFreetext as boolean) || false,
        multiSelect: data.multiSelect === true,
        context: (data.context as string) || '',
        toolCallId: (data.toolCallId as string) || '',
        answered: false,
      }
      next.askedQuestions = new Map(next.askedQuestions)
      next.askedQuestions.set(questionInfo.toolCallId, questionInfo)
      break
    }
    case 'dashboard_created': {
      // Extension event: dispatched to registered RenderPlugins AND stored in core maps.
      const dashInfo: CreatedDashboardInfo = {
        dashboardId: data.dashboardId as string,
        customer: data.customer as string,
        config: data.config as CreatedDashboardInfo['config'],
        viewUrl: data.viewUrl as string,
        toolCallId: (data.toolCallId as string) || '',
      }
      next.createdDashboards = new Map(next.createdDashboards)
      next.createdDashboards.set(dashInfo.dashboardId, dashInfo)
      break
    }
    case 'artifact_registered': {
      const artifact: ArtifactInfo = {
        filePath: data.filePath as string,
        fileName: (data.filePath as string).split('/').pop() || '',
        label: data.label as string,
        description: data.description as string,
        artifactType: data.artifactType as ArtifactInfo['artifactType'],
        source: 'registered',
        status: 'live',
      }
      // Chief of Staff: a file a sub-agent or workflow agent made says which
      // (the control server tags it). It is listed, and drawn in that agent's
      // drawer, but never pinned into the main conversation.
      if (typeof data.agentId === 'string' && data.agentId) artifact.agentId = data.agentId
      if (typeof data.parentToolUseId === 'string' && data.parentToolUseId) artifact.parentToolUseId = data.parentToolUseId
      const delegated = !!(artifact.agentId || artifact.parentToolUseId)
      const idx = next.artifacts.findIndex(a => a.filePath === artifact.filePath)
      if (idx >= 0) {
        next.artifacts = [...next.artifacts]
        // Never downgrade a webapp identity: iterating on the entry HTML fires
        // write_file auto-registrations for the same path, and a 'code' twin
        // would make the inline preview render source instead of the iframe.
        const existing = next.artifacts[idx]
        if (existing.artifactType === 'webapp' && artifact.artifactType !== 'webapp') {
          next.artifacts[idx] = { ...artifact, artifactType: 'webapp', status: existing.status, id: existing.id, updatedAt: existing.updatedAt }
        } else if (existing.artifactType === 'webapp') {
          // A web app registered again keeps its last capture's updatedAt until
          // loadArtifacts brings the new one, so the frame reloads once.
          next.artifacts[idx] = { ...artifact, id: existing.id, updatedAt: existing.updatedAt }
        } else {
          next.artifacts[idx] = artifact
        }
      } else {
        next.artifacts = [...next.artifacts, artifact]
      }

      // Associate with current message for inline display, once per path
      if (next.currentMessage && !delegated) {
        const messageId = next.currentMessage.id
        next.messages = next.messages.map(m =>
          m.id === messageId && !m.autoArtifactPaths?.includes(artifact.filePath)
            ? { ...m, autoArtifactPaths: [...(m.autoArtifactPaths || []), artifact.filePath] }
            : m
        )
      }
      break
    }
    case 'webapp_ready': {
      // Extension event: also dispatched to registered RenderPlugins.
      // Webapp files have been extracted to blob storage — update artifact status
      const artifactId = data.artifactId as string
      const webappFilePath = data.filePath as string
      if (webappFilePath) {
        const idx = next.artifacts.findIndex(a => a.filePath === webappFilePath)
        if (idx >= 0) {
          next.artifacts = [...next.artifacts]
          next.artifacts[idx] = { ...next.artifacts[idx], status: 'extracted' }
        }
      }
      // Also try matching by artifactId if we have it stored
      if (artifactId && !next.artifacts.some(a => a.filePath === webappFilePath && a.status === 'extracted')) {
        // Artifact may have been registered with a different path format — mark all webapp artifacts
        next.artifacts = next.artifacts.map(a =>
          a.artifactType === 'webapp' && a.status === 'live'
            ? { ...a, status: 'extracted' }
            : a
        )
      }
      break
    }
    case 'activity_update': {
      const phase = data.phase as string
      const label = data.label as string
      const toolName = data.toolName as string | undefined
      const categoryMap: Record<string, ActivityStatus['category']> = {
        thinking: 'thinking',
        writing: 'writing',
        preparing_tool: 'preparing_tool',
        processing: 'thinking',
        processing_results: 'thinking',
      }
      const category = categoryMap[phase] || 'thinking'
      // Don't override active tool status (tool_use_start takes priority)
      if (next.activityStatus?.category !== 'tool') {
        next.activityStatus = { label, category, toolName }
      }
      break
    }
    case 'tool_input_delta': {
      next.toolInputBuffer = next.toolInputBuffer + (data.partialJson as string)
      // Collapse inlined images before taking the tail, or the status line's
      // 80-character window is 80 characters of base64 (#1347 item 13).
      const readable = collapseBase64(next.toolInputBuffer)
      const preview = readable.length > 80
        ? '...' + readable.slice(-80)
        : readable
      if (next.activityStatus?.category === 'preparing_tool' || next.activityStatus?.category === 'tool') {
        next.activityStatus = { ...next.activityStatus, toolInputPreview: preview }
      }
      break
    }
    case 'system_status': {
      const status = data.status as string
      const statusLabels: Record<string, string> = {
        init: 'Initializing agent...',
        compacting: 'Compacting conversation history...',
        ready: 'Ready',
        auth: 'Authenticating...',
      }
      const label = status === 'retrying'
        ? retryingLabel((data.details as Record<string, unknown> | undefined) ?? {})
        : statusLabels[status] || `System: ${status}`
      if (status !== 'ready') {
        next.activityStatus = { label, category: 'system' }
      }
      break
    }
    case 'page_tool_request': {
      // Extension event: also dispatched to registered RenderPlugins.
      next.pendingPageToolRequest = {
        toolName: data.toolName as string,
        toolCallId: data.toolCallId as string,
      }
      break
    }
    case 'page_tool_call': {
      // A tool the page carries out (plan M5). Fire and forget: the model
      // was told "sent to the page" and the turn carries on.
      const name = data.name as string | undefined
      const toolCallId = (data.toolCallId as string) || ''
      const id = (data.id as string) || toolCallId
      if (!name || !id || next.pageToolCalls.some(c => c.id === id)) break
      next.pageToolCalls = [...next.pageToolCalls, {
        id,
        toolCallId,
        name,
        arguments: (data.arguments as Record<string, unknown>) || {},
      }]
      break
    }
    case 'settings_updated': {
      // Extension event: also dispatched to registered RenderPlugins.
      const { toolCallId, settings } = data as { toolCallId: string; settings: Record<string, unknown> }
      next.pendingSettingsUpdate = { toolCallId, settings }
      break
    }
    case 'session_info': {
      next.sessionInfo = {
        tools: (data.tools as string[]) || [],
        model: (data.model as string) || '',
        mcpServers: (data.mcpServers as AgentSessionInfo['mcpServers']) || [],
      }
      break
    }
    case 'hook_event': {
      const hookType = data.hookType as HookEventInfo['hookType']
      const payload = (data.payload as Record<string, unknown>) || {}
      const toolUseId = (payload.tool_use_id || payload.toolUseId) as string | undefined
      const toolName = (payload.tool_name || payload.toolName) as string | undefined
      const hookEvent: HookEventInfo = {
        hookType,
        toolName,
        toolUseId,
        timestamp: event.timestamp,
        payload,
      }
      if (toolUseId) {
        const tc = next.toolCalls.get(toolUseId)
        if (tc) {
          next.toolCalls = new Map(next.toolCalls)
          next.toolCalls.set(toolUseId, {
            ...tc,
            hookEvents: [...(tc.hookEvents || []), hookEvent],
          })
          next.messages = updateToolCallsOnMessages(next.messages, next.currentMessage, next.toolCalls)
        }
      }
      if ((hookType === 'pre_tool' || hookType === 'post_tool') && next.activityStatus?.category !== 'tool') {
        const label = hookType === 'pre_tool'
          ? `Pre-processing${toolName ? `: ${toolName}` : ''}`
          : `Post-processing${toolName ? `: ${toolName}` : ''}`
        next.activityStatus = { label, category: 'thinking', toolName }
      }
      break
    }
    case 'subagent_event': {
      const d = data as unknown as SubagentEventData
      const subEvent = d.event
      const agentType = d.agentType
      next.subagentEvents = [...next.subagentEvents, {
        event: subEvent,
        agentId: (d.agentId ?? d.taskId) as string,
        agentType,
        result: d.result,
        timestamp: event.timestamp,
      }]
      if (subEvent === 'start' && agentType) {
        next.activityStatus = { label: `Sub-agent: ${agentType}`, category: 'thinking' }
      }
      applySubagentEvent(next, d)
      break
    }
    case 'workflow_event': {
      applyWorkflowEvent(next, data as unknown as WorkflowEventData)
      break
    }
    case 'user_message': {
      const msgId = (data.id as string) || `user-${event.timestamp}`
      const content = (data.content as string) || ''
      if (!next.messages.find(m => m.id === msgId)) {
        next.messages = [...next.messages, {
          id: msgId,
          role: 'user' as const,
          content,
          timestamp: (data.timestamp as string) || event.timestamp,
        }]
      }
      break
    }
    case 'session_notice': {
      // A sentence from the host about this turn (a chat started in a new
      // working folder). It is kept on the message that started the turn, so
      // a replay of the same event leaves the same state.
      const message = data.message as string | undefined
      const last = next.messages[next.messages.length - 1]
      if (data.kind === 'skills_refreshed' && Array.isArray(data.skills)) {
        // The skills the chat reloaded before this turn (#2020): kept as
        // data, so each reader sees the times in their own time zone.
        if (!last || last.skillsRefreshed) break
        const skills = (data.skills as Array<Record<string, unknown>>)
          .filter(s => typeof s?.name === 'string')
          .map(s => ({ name: s.name as string, savedAt: typeof s.saved_at === 'number' ? s.saved_at : 0 }))
        next.messages = [...next.messages.slice(0, -1), { ...last, skillsRefreshed: skills }]
        break
      }
      if (!message || !last || last.notice === message) break
      next.messages = [...next.messages.slice(0, -1), { ...last, notice: message }]
      break
    }
    case 'user_message_queued': {
      const id = data.id as string
      if (!next.queuedMessages.some(m => m.id === id)) {
        next.queuedMessages = [...next.queuedMessages, {
          id,
          role: 'user' as const,
          content: (data.content as string) || '',
          timestamp: event.timestamp,
          queued: true,
        }]
      }
      break
    }
    case 'queued_message_sent': {
      const id = data.id as string
      const queued = next.queuedMessages.find(m => m.id === id)
      if (queued) {
        next.queuedMessages = next.queuedMessages.filter(m => m.id !== id)
        const { queued: _queued, ...sent } = queued
        next.messages = [...next.messages, { ...sent, timestamp: event.timestamp }]
      }
      break
    }
    case 'session_jobs_changed': {
      next.sessionJobs = (data.jobs as string[] | undefined) ?? []
      break
    }
    case 'skill_installed': {
      const d = event.data as SkillInstalledEventData
      const info: InstalledSkillInfo = { id: d.id, name: d.name, visibility: d.visibility }
      next.installedSkills = [...next.installedSkills.filter(s => s.name !== d.name), info]
      break
    }
    case 'skill_hoisted': {
      const d = event.data as SkillHoistedEventData
      if (d?.name) next.hoistedSkill = { name: d.name, at: event.timestamp }
      break
    }
    case 'heartbeat': {
      // No state change — side effects handled externally
      break
    }
    case 'query_complete': {
      next.isStreaming = false
      next.activityStatus = null
      // A stopped turn (#1295). The sandbox emits query_complete with
      // status 'cancelled' when the user pressed Stop. Nothing is left
      // "running": every in-flight tool call becomes 'cancelled', and the
      // message that was being written is captioned as stopped. Live and
      // replay share this branch, so a restored session shows the same.
      // An 'interrupted' turn (the host lost it — see checkOrphanedTurn in the
      // Go runner) settles the same way, captioned for what happened.
      // A 'error' turn (the model call itself failed, e.g. the agent proxy
      // rejected it — #1575) settles the same way too, captioned with the
      // reason instead of "Stopped"/"Interrupted".
      const interrupted = data.status === 'interrupted'
      const failed = data.status === 'error'
      if (data.status === 'cancelled' || interrupted || failed) {
        // Sub-agents and workflows still running stop with the turn, and so
        // do the tools they had in flight.
        settleDelegated(next)
        // Compute the "running -> cancelled" rule ONCE, as a set of ids, then
        // apply it everywhere a tool call is rewritten (the live map and
        // every message's toolCalls array).
        const cancelledIds = new Set<string>()
        next.toolCalls = new Map(next.toolCalls)
        for (const [id, tc] of next.toolCalls) {
          if (tc.status === 'running') {
            cancelledIds.add(id)
            next.toolCalls.set(id, { ...tc, status: 'cancelled' })
          }
        }
        // Caption the last assistant message the reader can actually see —
        // the harness closes and re-opens a message around every tool result,
        // so the open one at stop time is often empty.
        let stoppedMessageId: string | undefined
        let lastAssistantId: string | undefined
        for (let i = next.messages.length - 1; i >= 0; i--) {
          const m = next.messages[i]
          if (m.role !== 'assistant') continue
          if (lastAssistantId === undefined) lastAssistantId = m.id
          if (m.content.trim() || m.thinking || (m.toolCalls?.length ?? 0) > 0) {
            stoppedMessageId = m.id
            break
          }
        }
        stoppedMessageId = stoppedMessageId ?? lastAssistantId

        const failureReason = failed
          ? humanizeFailureReason((data.error as string | undefined) || 'The turn failed before the agent could answer.')
          : undefined

        if (failed && stoppedMessageId === undefined) {
          // The turn produced no assistant message at all (e.g. the very
          // first model call of the turn failed) — append a stub so the
          // failure still shows in the chat rather than nothing (#1575).
          next.messages = [...next.messages, {
            id: `failed-${event.timestamp}`,
            role: 'assistant' as const,
            content: '',
            timestamp: event.timestamp,
            stopped: true,
            failed: failureReason,
          }]
          next.currentMessage = null
          break
        }

        next.messages = next.messages.map(m => {
          const cancelledTools = m.toolCalls?.some(tc => cancelledIds.has(tc.id))
            ? m.toolCalls.map(tc => cancelledIds.has(tc.id) ? { ...tc, status: 'cancelled' as const } : tc)
            : m.toolCalls
          if (cancelledTools === m.toolCalls && m.id !== stoppedMessageId) return m
          return {
            ...m,
            ...(cancelledTools !== m.toolCalls ? { toolCalls: cancelledTools } : {}),
            ...(m.id === stoppedMessageId
              ? { stopped: true, ...(interrupted ? { interrupted: true } : {}), ...(failed ? { failed: failureReason } : {}) }
              : {}),
          }
        })
        next.currentMessage = null
        break
      }
      // If result exists but no assistant messages, add one
      const result = data.result as string | undefined
      if (result && !next.currentMessage) {
        const hasAssistant = next.messages.some(m => m.role === 'assistant' && m.content.trim())
        if (!hasAssistant) {
          next.messages = [...next.messages, {
            id: `result-${event.timestamp}`,
            role: 'assistant' as const,
            content: result,
            timestamp: event.timestamp,
          }]
        }
      }
      break
    }
    case 'error': {
      const message = (data.message as string) || 'Unknown error'
      next.error = isModelOverloaded(message) ? MODELS_BUSY_MESSAGE : message
      next.isStreaming = false
      next.activityStatus = null
      break
    }
  }

  return next
}

/**
 * What the chat says when the model provider is short of capacity, in place
 * of the SDK's "API Error: 503 Overloaded. This is a server-side issue …"
 * (Matt, 27 Oct 2026: say we're on it, that there are options, and that it is
 * most likely global demand, without promising anything).
 */
export const MODELS_BUSY_MESSAGE =
  "The AI models are very busy right now, most likely from global demand for agents. We're on it and have other options we can switch you to quickly, so please try again in a moment."

/**
 * An overloaded provider: Bedrock's 503 "Overloaded" (overloaded_error), the
 * Anthropic API's 529, or a 429 throttle, in the SDK's echo or as raw JSON.
 * goapi's own 503s ("could not verify this session") are not overloads.
 */
function isModelOverloaded(raw: string): boolean {
  return /overloaded/i.test(raw) || /API Error:\s*(429|529)\b/i.test(raw)
}

/**
 * A failure reason may arrive as the proxy's raw JSON error body, or as the
 * SDK's own "API Error: <code> <json>" echo of it (#1575) — either way, pull
 * out the human `message` rather than showing raw JSON in the chat.
 *
 * The SDK may prefix the echo ("Failed to authenticate. API Error: 403 …" for
 * any 403, which is how the proxy refuses a call once the team's budget is
 * used) and may have already pulled the message out of the JSON. Either way
 * the text after the status code is what the person should read.
 */
function humanizeFailureReason(raw: string): string {
  if (isModelOverloaded(raw)) return MODELS_BUSY_MESSAGE
  const trimmed = raw.trim()
  const apiErrorMatch = trimmed.match(/API Error:\s*\d+\s*(.*)$/is)
  const jsonCandidate = (apiErrorMatch ? apiErrorMatch[1] : trimmed).trim()
  if (jsonCandidate.startsWith('{')) {
    try {
      const parsed = JSON.parse(jsonCandidate) as { message?: string; error?: { message?: string } }
      const message = parsed.error?.message || parsed.message
      if (message) return message
    } catch {
      // Not valid JSON — fall through to the raw text.
    }
    return raw
  }
  if (apiErrorMatch && jsonCandidate) return jsonCandidate
  return raw
}

function updateToolCallsOnMessages(
  messages: AgentMessage[],
  currentMessage: { id: string; content: string } | null,
  toolCalls: Map<string, ToolCallInfo>,
): AgentMessage[] {
  if (!currentMessage) return messages
  const toolCallsArray = Array.from(toolCalls.values())
  return messages.map(m =>
    m.id === currentMessage.id ? { ...m, toolCalls: toolCallsArray } : m
  )
}

/**
 * Update a single tool call by ID across all messages.
 * Used when tool_use_end arrives after a message_start reset the toolCalls Map,
 * so the tool lives in a previous message's toolCalls array.
 */
function updateToolInMessages(
  messages: AgentMessage[],
  toolCallId: string,
  updates: Partial<ToolCallInfo>,
): AgentMessage[] {
  return messages.map(msg => {
    if (!msg.toolCalls) return msg
    const idx = msg.toolCalls.findIndex(tc => tc.id === toolCallId)
    if (idx === -1) return msg
    return {
      ...msg,
      toolCalls: msg.toolCalls.map((tc, i) =>
        i === idx ? { ...tc, ...updates } : tc
      ),
    }
  })
}

/** The status line while the CLI tries a failed model call again. A 401 or
 *  403 is our credentials, which no amount of waiting fixes, so it says so. */
function retryingLabel(details: Record<string, unknown>): string {
  const attempt = typeof details.attempt === 'number' ? details.attempt : undefined
  const max = typeof details.maxRetries === 'number' ? details.maxRetries : undefined
  const count = attempt !== undefined && max !== undefined ? ` (${attempt} of ${max})` : ''
  const refused = details.errorStatus === 401 || details.errorStatus === 403
  const cause = refused
    ? 'The AI model refused our credentials, so this is a setup problem, not your request.'
    : 'The AI model is not answering.'
  return `${cause} Trying again${count}...`
}

// ── Sub-agents and workflows (Chief of Staff mode) ─────────────────────────

/** Event types a sub-agent's stream produces, tagged with `parentToolUseId`. */
const NESTED_EVENT_TYPES = new Set<AgentSSEEvent['type']>([
  'message_start', 'message_end', 'content_delta', 'thinking_delta',
  'tool_use_start', 'tool_use_end', 'tool_input_delta', 'tool_progress',
])

/** The SDK's sub-agent tool (`Task` before it was renamed `Agent`). */
export function isSubagentTool(name: string): boolean {
  return name === 'Agent' || name === 'Task'
}

export function isWorkflowTool(name: string): boolean {
  return name === 'Workflow'
}

function newSubagent(tc: ToolCallInfo): SubagentInfo {
  const input = tc.input || {}
  return {
    agentType: typeof input.subagent_type === 'string' ? input.subagent_type : undefined,
    description: typeof input.description === 'string' ? input.description : undefined,
    status: 'running',
    text: '',
    toolCalls: [],
  }
}

/**
 * The run id a Workflow tool result names: the JSON result's `runId`
 * (`{"status":"async_launched",…,"runId":"wf_…"}`), else the first `wf_…`
 * in its text.
 */
export function workflowRunId(output: unknown): string | undefined {
  const text = typeof output === 'string' ? output : output == null ? '' : JSON.stringify(output)
  try {
    const parsed = JSON.parse(text) as { runId?: unknown } | null
    if (parsed && typeof parsed === 'object' && typeof parsed.runId === 'string' && parsed.runId) return parsed.runId
  } catch { /* not JSON: look in the text */ }
  return /\bwf_[a-z0-9-]+/.exec(text)?.[0]
}

function newWorkflow(tc: ToolCallInfo): WorkflowInfo {
  const input = tc.input || {}
  return {
    name: typeof input.name === 'string' ? input.name : undefined,
    status: 'running',
    agents: [],
  }
}

/** A tool call by id: the live map first, then the messages, newest first. */
function findToolCall(state: AgentEventState, id: string): ToolCallInfo | undefined {
  const live = state.toolCalls.get(id)
  if (live) return live
  for (let i = state.messages.length - 1; i >= 0; i--) {
    const tc = state.messages[i].toolCalls?.find(t => t.id === id)
    if (tc) return tc
  }
  return undefined
}

/**
 * Replace a tool call wherever it is held: the live map (when it is there) and
 * the message that carries it. One object is written to both, so the two
 * copies never drift apart.
 */
function setToolCall(next: AgentEventState, id: string, updated: ToolCallInfo): void {
  if (next.toolCalls.has(id)) {
    next.toolCalls = new Map(next.toolCalls)
    next.toolCalls.set(id, updated)
  }
  for (let i = next.messages.length - 1; i >= 0; i--) {
    const m = next.messages[i]
    const idx = m.toolCalls?.findIndex(t => t.id === id) ?? -1
    if (idx === -1) continue
    const toolCalls = m.toolCalls!.slice()
    toolCalls[idx] = updated
    next.messages = next.messages.slice()
    next.messages[i] = { ...m, toolCalls }
    return
  }
}

function isTerminal(status: SubagentInfo['status']): boolean {
  return status !== 'running'
}

function reduceNestedEvent(
  next: AgentEventState,
  event: AgentSSEEvent,
  data: Record<string, unknown>,
  parentId: string,
): AgentEventState {
  const parent = findToolCall(next, parentId)
  // A parent we have never seen: drop the event rather than let a sub-agent's
  // words reach the main reply.
  if (!parent) return next

  if (parent.workflow || isWorkflowTool(parent.name)) {
    // A workflow's agents are summarised by its task events; of their own
    // stream only the latest tool is kept, for the card's "last tool" line.
    if (event.type === 'tool_use_start' && typeof data.toolName === 'string') {
      const workflow = parent.workflow ?? newWorkflow(parent)
      setToolCall(next, parentId, { ...parent, workflow: { ...workflow, lastToolName: data.toolName } })
    }
    return next
  }

  const sub = parent.subagent ?? newSubagent(parent)
  let updated: SubagentInfo = sub
  switch (event.type) {
    case 'message_start': {
      if (sub.text && !sub.text.endsWith('\n\n')) updated = { ...sub, text: sub.text + '\n\n' }
      break
    }
    case 'content_delta': {
      updated = { ...sub, text: sub.text + ((data.delta as string) || '') }
      break
    }
    case 'thinking_delta': {
      updated = { ...sub, thinking: (sub.thinking || '') + ((data.delta as string) || '') }
      break
    }
    case 'tool_use_start': {
      const id = data.toolCallId as string
      if (!id || sub.toolCalls.some(t => t.id === id)) break
      const toolName = (data.toolName as string) || ''
      const toolInput = (data.input as Record<string, unknown>) || {}
      updated = { ...sub, toolCalls: [...sub.toolCalls, { id, name: toolName, input: toolInput, status: 'running' }] }
      const displayName = getToolDisplayName(toolName, toolInput)
      next.activityStatus = {
        label: `${sub.description || sub.agentType || 'Sub-agent'}: ${displayName}`,
        detail: getToolSummary(toolName, toolInput) || undefined,
        category: 'tool',
        toolName,
        toolInput,
      }
      break
    }
    case 'tool_use_end': {
      const id = data.toolCallId as string
      if (!sub.toolCalls.some(t => t.id === id)) break
      const status: ToolCallInfo['status'] = data.isError ? 'error' : 'complete'
      updated = {
        ...sub,
        toolCalls: sub.toolCalls.map(t => t.id === id ? { ...t, status, output: data.output as string } : t),
      }
      break
    }
    case 'tool_progress': {
      const id = data.toolUseId as string
      if (!sub.toolCalls.some(t => t.id === id)) break
      updated = {
        ...sub,
        toolCalls: sub.toolCalls.map(t => t.id === id ? { ...t, elapsedSeconds: data.elapsedSeconds as number } : t),
      }
      break
    }
    default:
      // message_end and tool_input_delta change nothing in the sub-tree, and
      // must not touch the main message or the main input buffer.
      break
  }
  if (updated !== parent.subagent) setToolCall(next, parentId, { ...parent, subagent: updated })
  return next
}

/** Remember which tool call an agent or task id belongs to. */
function linkTask(next: AgentEventState, toolUseId: string, ...ids: Array<string | undefined>): void {
  const fresh = ids.filter((id): id is string => !!id && next.delegatedTasks[id] !== toolUseId)
  if (fresh.length === 0) return
  next.delegatedTasks = { ...next.delegatedTasks }
  for (const id of fresh) next.delegatedTasks[id] = toolUseId
}

function resolveTask(next: AgentEventState, d: { toolUseId?: string; taskId?: string; agentId?: string }): string | undefined {
  if (d.toolUseId) {
    linkTask(next, d.toolUseId, d.agentId, d.taskId)
    return d.toolUseId
  }
  return (d.agentId && next.delegatedTasks[d.agentId]) || (d.taskId && next.delegatedTasks[d.taskId]) || undefined
}

function stoppedStatus(d: { event: string; status?: SubagentInfo['status'] }, current: SubagentInfo['status']): SubagentInfo['status'] {
  if (d.event !== 'stop') return current
  if (d.status) return d.status
  // A hook's stop says nothing of how it ended; it never overrides a status a
  // task notification already gave.
  return isTerminal(current) ? current : 'completed'
}

function applySubagentEvent(next: AgentEventState, d: SubagentEventData): void {
  const toolUseId = resolveTask(next, d)
  if (!toolUseId) return
  const tc = findToolCall(next, toolUseId)
  if (!tc) return

  // A workflow's own agents, if the runner reports them as agent tasks of the
  // Workflow call: one row each.
  if (tc.workflow || isWorkflowTool(tc.name)) {
    const workflow = tc.workflow ?? newWorkflow(tc)
    const rowId = d.taskId || d.agentId
    if (!rowId) return
    const existing = workflow.agents.find(a => a.id === rowId)
    const row = {
      id: rowId,
      label: d.description ?? existing?.label ?? d.agentType,
      status: stoppedStatus(d, existing?.status ?? 'running'),
    }
    const agents = existing ? workflow.agents.map(a => a.id === rowId ? row : a) : [...workflow.agents, row]
    setToolCall(next, toolUseId, { ...tc, workflow: { ...workflow, agents } })
    return
  }

  const sub = tc.subagent ?? newSubagent(tc)
  const updated: SubagentInfo = {
    ...sub,
    agentType: sub.agentType ?? d.agentType,
    description: sub.description ?? d.description,
    taskId: sub.taskId ?? d.taskId,
    summary: d.summary ?? sub.summary,
    usage: d.usage ?? sub.usage,
    result: d.result ?? sub.result,
    status: stoppedStatus(d, sub.status),
  }
  setToolCall(next, toolUseId, { ...tc, subagent: updated })
}

function applyWorkflowEvent(next: AgentEventState, d: WorkflowEventData): void {
  const toolUseId = resolveTask(next, d)
  if (!toolUseId) return
  const tc = findToolCall(next, toolUseId)
  if (!tc) return
  const workflow = tc.workflow ?? newWorkflow(tc)
  const updated: WorkflowInfo = {
    ...workflow,
    name: d.workflowName ?? workflow.name,
    description: d.description ?? workflow.description,
    taskId: workflow.taskId ?? d.taskId,
    summary: d.summary ?? workflow.summary,
    lastToolName: d.lastToolName ?? workflow.lastToolName,
    usage: d.usage ?? workflow.usage,
    status: stoppedStatus(d, workflow.status),
  }
  setToolCall(next, toolUseId, { ...tc, workflow: updated })
}

/** Stop: every running sub-agent or workflow stops, its running tools cancelled. */
function settleOne(tc: ToolCallInfo): ToolCallInfo {
  let out = tc
  if (tc.subagent && tc.subagent.status === 'running') {
    out = {
      ...out,
      subagent: {
        ...tc.subagent,
        status: 'stopped',
        toolCalls: tc.subagent.toolCalls.map(t => t.status === 'running' ? { ...t, status: 'cancelled' as const } : t),
      },
    }
  }
  if (tc.workflow && tc.workflow.status === 'running') {
    out = {
      ...out,
      workflow: {
        ...tc.workflow,
        status: 'stopped',
        agents: tc.workflow.agents.map(a => a.status === 'running' ? { ...a, status: 'stopped' as const } : a),
      },
    }
  }
  return out
}

function settleDelegated(next: AgentEventState): void {
  const needs = (tc: ToolCallInfo) => tc.subagent?.status === 'running' || tc.workflow?.status === 'running'
  if ([...next.toolCalls.values()].some(needs)) {
    next.toolCalls = new Map([...next.toolCalls].map(([id, tc]) => [id, needs(tc) ? settleOne(tc) : tc]))
  }
  if (next.messages.some(m => m.toolCalls?.some(needs))) {
    next.messages = next.messages.map(m => m.toolCalls?.some(needs)
      ? { ...m, toolCalls: m.toolCalls.map(tc => needs(tc) ? settleOne(tc) : tc) }
      : m)
  }
}
